import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import AddIcon from '@material-ui/icons/Add';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Page, PageHeader, SearchBar } from 'components';
import { UsersTable, UserOrgSelector, UserForm } from './components';
import { Dialog, Box } from '@material-ui/core';
import _ from 'lodash';
import { Roles } from 'utils/authHelpers';
import { DashboardHelpers } from 'utils';
import UserInviteForm from './components/UserInviteForm';
import { ConfigContext } from 'context/ConfigContext';
import { FormControlLabel, FormGroup, Switch, Grid } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    minHeight: '100%',
    flex: 1,
  },
  spacing: {
    marginBottom: theme.spacing(2),
  },
}));

const UsersPage = ({
  setUserOrgName,
  setFilterByOrg,
  fetchUsers,
  instancePermissions,
  instanceRoles,
  users,
  isFetchingUsers,
  organizations,
  filterByOrg,
  selectedOrganization,
  currentUserRoles,
  saveUser,
  inviteUser,
  isSuper,
}) => {
  const classes = useStyles();
  const { client_id } = useContext(ConfigContext);
  const { t } = useTranslation();
  const [modal, setModal] = useState(false);
  const [inviteToggle, setInviteToggle] = useState(true);
  const [inviteUserOpen, setInviteUserOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedUser, setSelectedUser] = useState();

  const toggleInviteUserOpen = () => {
    setInviteUserOpen(!inviteUserOpen);
  };

  useEffect(() => {
    if (isSuper && (!users || users?.length === 0)) {
      if (selectedOrganization) {
        fetchUsers(selectedOrganization.id);
        setFilterByOrg(selectedOrganization.name);
      } else {
        fetchUsers();
      }
    } else if (!isSuper && (!users || users?.length === 0) && selectedOrganization) {
      fetchUsers(selectedOrganization.id);
      setFilterByOrg(selectedOrganization.name);
    }
  }, []);

  const buildInviteUser = (email, organization) => {
    const user = {
      email,
      organization_id: organization?.id,
      password: '-------',
      permissions_update: DashboardHelpers.DefaultUserPermissions,
      roles_update: { add: [{ role: Roles.User }] },
    };

    inviteUser(email, client_id, user).then((response) => {
      if (response && !response.error) {
        setInviteUserOpen(false);
        fetchUsers();
      }
    });
  };

  return (
    <Page title={t('user_admin')} className={classes.root}>
      <Dialog open={modal} onClose={() => setModal(false)} scroll="paper">
        <Box>
          <UserForm
            handleCloseModal={() => setModal(!modal)}
            instancePermissions={instancePermissions}
            instanceRoles={instanceRoles}
            userToEdit={selectedUser}
            onSave={saveUser}
            organizations={organizations}
            selectedOrganization={selectedOrganization}
            currentUserRoles={currentUserRoles}
          />
        </Box>
      </Dialog>
      <UserInviteForm
        open={inviteUserOpen}
        onClose={toggleInviteUserOpen}
        onInvite={buildInviteUser}
        selectedOrganization={selectedOrganization}
        isSuper={isSuper}
      />
      <PageHeader
        className={classes.spacing}
        title="user_admin"
        actionFab={true}
        actionHref={() => {
          if (!isSuper || inviteToggle) {
            toggleInviteUserOpen();
          } else {
            setModal(true);
          }
          setSelectedUser(undefined);
        }}
        action={_.includes(currentUserRoles, Roles.Super) && !inviteToggle ? t('add_user') : t('invite_user')}
        icon={<AddIcon />}
      />
      {_.includes(currentUserRoles, Roles.Super) && (
        <div className={classes.spacing}>
          <UserOrgSelector
            setUserOrgName={setUserOrgName}
            organizations={organizations}
            fetchUsers={fetchUsers}
            setFilterByOrg={setFilterByOrg}
            filterByOrg={filterByOrg}
          />
        </div>
      )}
      {_.includes(currentUserRoles, Roles.Super) && (
        <Grid container justifyContent="end" justifyItems="end">
          <Grid item>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={inviteToggle} onChange={() => setInviteToggle(!inviteToggle)} />}
                label={'User Add/Invite'}
              />
            </FormGroup>
          </Grid>
        </Grid>
      )}

      <SearchBar className={classes.spacing} selector={searchText} setSearchText={setSearchText} />
      <UsersTable
        searchText={searchText}
        setModal={setModal}
        setSelectedUser={setSelectedUser}
        className={classes.spacing}
        users={users}
        isFetchingUsers={isFetchingUsers}
        organizations={organizations}
        selectedUser={selectedUser}
      />
    </Page>
  );
};

UsersPage.propTypes = {
  setUserOrgName: PropTypes.func.isRequired,
  fetchUsers: PropTypes.func.isRequired,
  setFilterByOrg: PropTypes.func.isRequired,
  setSelectedUser: PropTypes.func.isRequired,
  inviteUser: PropTypes.func.isRequired,
  saveUser: PropTypes.func.isRequired,
  instancePermissions: PropTypes.object,
  instanceRoles: PropTypes.object,
  users: PropTypes.array,
  isFetchingUsers: PropTypes.bool,
  organizations: PropTypes.array,
  filterByOrg: PropTypes.string,
  selectedOrganization: PropTypes.object,
  currentUserRoles: PropTypes.array,
  selectedUser: PropTypes.object,
  isSuper: PropTypes.bool,
};

UsersPage.defaultProps = {
  instancePermissions: undefined,
  instanceRoles: undefined,
  users: [],
  isFetchingUsers: true,
  organizations: [],
  filterByOrg: '',
  selectedOrganization: undefined,
  currentUserRoles: [],
  selectedUser: undefined,
  isSuper: false,
};

export default UsersPage;
